import { useCallback } from "react";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useBoardPermissions } from "@/react/components/entityViews/settings/hooks/useBoardPermissions";
import { VIEW_TYPES } from "@/other/constants/OtherKeys";
import { getApiErrorMessage } from "./errors";
import { INTEGRATION_SETTING_KEYS } from "./constants";
import { reportIntegrationToast } from "./reportIntegrationToast";
import { useOpenAIKeyModal } from "./OpenAIKeyModalProvider";
import type { IntegrationProvider, ReportErrorOptions } from "./types";

export function useIntegrationErrorReporter() {
  const { selectedBoard, viewManager } = useSwivuBryntumContext();
  const { isBoardOwner, canWrite } = useBoardPermissions();
  const openAiKeyModal = useOpenAIKeyModal();

  const canOpenCompanyCamSettings = Boolean(isBoardOwner && selectedBoard?.record && viewManager);

  const openCompanyCamSettings = useCallback(() => {
    if (!viewManager || !isBoardOwner) return;

    viewManager.openAnonymousViewByType({
      viewType: VIEW_TYPES.settings,
      viewName: "Settings",
      otherParams: {
        setting: INTEGRATION_SETTING_KEYS.company_cam,
      },
    });
  }, [viewManager, isBoardOwner]);

  const canFixIntegration = useCallback(
    (provider: IntegrationProvider) => {
      if (provider === "openai") {
        return Boolean(canWrite && openAiKeyModal);
      }
      return canOpenCompanyCamSettings;
    },
    [canWrite, canOpenCompanyCamSettings, openAiKeyModal]
  );

  const openIntegrationFix = useCallback(
    (provider: IntegrationProvider) => {
      if (provider === "openai") {
        openAiKeyModal?.open();
        return;
      }
      openCompanyCamSettings();
    },
    [openAiKeyModal, openCompanyCamSettings]
  );

  const reportError = useCallback(
    ({ error, provider, fallback, surface = "toast", title, showFixAction }: ReportErrorOptions): string => {
      const message = getApiErrorMessage(error, fallback);

      if (surface === "toast") {
        reportIntegrationToast({ error, provider, fallback, title, showFixAction });
      }

      return message;
    },
    []
  );

  return {
    reportError,
    openIntegrationFix,
    canFixIntegration,
  };
}
